import { findFacility } from "@penmate/react-shared/api/facilities";
import type { RawMessage } from "@/api/messages";
import { Button } from "@penmate/react-shared/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { US_STATES } from "@penmate/react-shared/lib/usStates";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EMESSAGE_MAX_SELECTED_PHOTOS } from "@/lib/images";
import { optionCardClass } from "@/components/checkout/optionCard";
import { useAutoAnimate } from "@formkit/auto-animate/react";

type ShippingMethod = "letter" | "emessage";

const getShippingMethod = (letter: RawMessage | null): ShippingMethod => {
  const v = (letter as any)?.shippingMethod ?? (letter as any)?.data?.shippingMethod;
  return String(v).toLowerCase() === "emessage" ? "emessage" : "letter";
};

const getMessageType = (letter: RawMessage | null): "letter" | "postcard" => {
  const t =
    (letter as any)?.messageType ?? (letter as any)?.data?.messageType ?? (letter as any)?.order_type;
  return String(t).toLowerCase() === "postcard" ? "postcard" : "letter";
};

const getFacility = (letter: RawMessage | null): any | null => {
  const eMessage = (letter as any)?.eMessage ?? (letter as any)?.data?.eMessage;
  return eMessage && typeof eMessage === "object" ? eMessage : null;
};

const getSelectedPhotosCount = (letter: RawMessage | null): number => {
  const v = (letter as any)?.selectedPhotos ?? (letter as any)?.data?.selectedPhotos ?? (letter as any)?.photos ?? [];
  return Array.isArray(v) ? v.length : 0;
};

const toStateAbbrev = (value: string): string => {
  const trimmed = value.trim();
  const upper = trimmed.toUpperCase();
  if (upper.length === 2) return upper;
  const match = US_STATES.find((s) => s.name === trimmed);
  return match ? match.abbrev : "";
};

const inferStateFromFacility = (facility: any | null): string => {
  if (!facility) return "";
  const fromState = typeof facility?.state === "string" ? toStateAbbrev(facility.state) : "";
  if (fromState) return fromState;
  const addr = typeof facility?.emessaging?.address === "string" ? facility.emessaging.address : "";
  if (!addr) return "";
  const m = addr.match(/,\s*([A-Z]{2})\b/);
  return m && m[1] ? toStateAbbrev(m[1]) : "";
};

const MIN_FACILITY_QUERY_LENGTH = 3;

export function DeliveryFacilitySheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialStep: "delivery" | "facility";
  checkoutLetter: RawMessage | null;
  lockShippingMethod?: boolean;
  defaultState?: string | null;
  onSave: (args: { shippingMethod: ShippingMethod; eMessage: any | null }) => Promise<void>;
}) {
  const { open, onOpenChange, initialStep, checkoutLetter, lockShippingMethod = false, defaultState, onSave } = props;
  const [contentAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();

  const isPostcard = getMessageType(checkoutLetter) === "postcard";
  const initialShippingMethod = getShippingMethod(checkoutLetter);
  const existingFacility = getFacility(checkoutLetter);

  const [step, setStep] = useState<"delivery" | "facility">(initialStep);
  const [shippingMethod, setShippingMethod] = useState<ShippingMethod>(initialShippingMethod);
  const [selectedState, setSelectedState] = useState<string>("");
  const [query, setQuery] = useState("");
  const [facilities, setFacilities] = useState<any[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<any | null>(existingFacility);
  const [facilitySearchOpen, setFacilitySearchOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [truncateConfirmOpen, setTruncateConfirmOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(initialStep);
    setShippingMethod(initialShippingMethod);
    setSelectedFacility(existingFacility);
    setSelectedState(inferStateFromFacility(existingFacility) || toStateAbbrev(defaultState || ""));
    setQuery(typeof existingFacility?.name === "string" ? existingFacility.name : "");
    setFacilities([]);
    setFacilitiesLoading(false);
    setFacilitySearchOpen(false);
    setSaveError(null);
    setIsSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStep, (checkoutLetter as any)?.id]);

  const selectedStateAbbrev = useMemo(() => toStateAbbrev(selectedState), [selectedState]);
  const canSearch =
    facilitySearchOpen && !!selectedStateAbbrev && query.trim().length >= MIN_FACILITY_QUERY_LENGTH;
  useEffect(() => {
    if (!open) return;
    if (!canSearch) {
      setFacilities([]);
      setFacilitiesLoading(false);
      return;
    }

    let cancelled = false;
      setFacilitiesLoading(true);
      const timer = window.setTimeout(() => {
        void (async () => {
          try {
            const res = await findFacility({ q: query.trim(), state: selectedStateAbbrev, email: true });
            if (cancelled) return;
            setFacilities(Array.isArray(res) ? res : []);
            setFacilitiesLoading(false);
          } catch (e) {
            if (cancelled) return;
            setFacilities([]);
            setFacilitiesLoading(false);
          }
        })();
      }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canSearch, open, query, selectedStateAbbrev]);

  const canSave = useMemo(() => {
    if (shippingMethod === "letter") return true;
    if (isPostcard) return false;
    return !!selectedFacility;
  }, [isPostcard, selectedFacility, shippingMethod]);

  const primaryLabel = shippingMethod === "emessage" ? "Save & Continue" : "Save";
  const selectedPhotosCount = useMemo(() => getSelectedPhotosCount(checkoutLetter), [checkoutLetter]);

  useEffect(() => {
    if (!facilitySearchOpen) return;
    if (!selectedStateAbbrev) return;
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [facilitySearchOpen, selectedStateAbbrev]);

  const onPickEmessage = () => {
    if (lockShippingMethod) return;
    if (isPostcard) return;
    setShippingMethod("emessage");
    setStep("facility");
  };

  const onPickLetter = () => {
    if (lockShippingMethod) return;
    setShippingMethod("letter");
    setStep("delivery");
    // Preserve selected facility so toggling back to E-message keeps it preselected (legacy parity).
    setFacilitySearchOpen(false);
  };

  const onSelectFacility = (f: any) => {
    setSelectedFacility(f);
    setQuery(typeof f?.name === "string" ? f.name : "");
    setFacilities([]);
    setFacilitiesLoading(false);
    setFacilitySearchOpen(false);
    // Keep focus behavior predictable inside the dialog.
    window.setTimeout(() => inputRef.current?.blur(), 0);
  };

  const openFacilitySearch = () => {
    setFacilitySearchOpen(true);
  };

  const closeFacilitySearch = () => {
    setFacilitySearchOpen(false);
    window.setTimeout(() => inputRef.current?.blur(), 0);
  };

  const save = async () => {
    if (!canSave) return;
    if (
      !isPostcard &&
      shippingMethod === "emessage" &&
      selectedPhotosCount > EMESSAGE_MAX_SELECTED_PHOTOS &&
      !truncateConfirmOpen
    ) {
      setSaveError(null);
      setTruncateConfirmOpen(true);
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      if (isPostcard) {
        await onSave({ shippingMethod: "letter", eMessage: null });
      } else if (shippingMethod === "letter") {
        await onSave({ shippingMethod: "letter", eMessage: null });
      } else {
        await onSave({ shippingMethod: "emessage", eMessage: selectedFacility });
      }
      onOpenChange(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    // NOTE: Radix Dialog's scroll-lock has caused non-scrollable overflow containers in our
    // Shadow DOM modals (seen previously in the postcard picker). Use non-modal dialog to avoid
    // scroll lock while still rendering as an overlay sheet.
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className="pm-sheet w-[calc(100vw-1rem)] max-w-md !p-0">
        <DialogTitle className="sr-only">Delivery</DialogTitle>
        <DialogDescription className="sr-only">
          Choose delivery method and confirm facility for e-message.
        </DialogDescription>

        <div className="relative flex min-h-0 flex-col">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Delivery</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {lockShippingMethod ? "Delivery method can’t be changed for a resend." : "Choose Print & Mail or E-message."}
              </div>
            </div>
          </div>

          <div
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
            style={{
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              touchAction: "pan-y",
            }}
            ref={contentAutoAnimateRef}
          >
            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                className={optionCardClass({
                  selected: shippingMethod === "letter",
                  disabled: lockShippingMethod,
                })}
                onClick={onPickLetter}
                disabled={lockShippingMethod}
                aria-disabled={lockShippingMethod}
                data-locked={lockShippingMethod ? "true" : "false"}
                data-testid="delivery-option-letter"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">Print & Mail</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      USPS First Class + Tracking
                    </div>
                  </div>
                  {shippingMethod === "letter" ? (
                    <span className="mt-0.5 text-emerald-600">
                      <CheckIcon className="size-4" aria-hidden="true" />
                    </span>
                  ) : null}
                </div>
              </button>

              <button
                type="button"
                className={optionCardClass({
                  selected: shippingMethod === "emessage",
                  disabled: isPostcard || lockShippingMethod,
                })}
                onClick={onPickEmessage}
                disabled={isPostcard || lockShippingMethod}
                data-testid="delivery-option-emessage"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium">E-message</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      1–3 days · Requires approval.
                    </div>
                  </div>
                  {shippingMethod === "emessage" ? (
                    <span className="mt-0.5 text-emerald-600">
                      <CheckIcon className="size-4" aria-hidden="true" />
                    </span>
                  ) : null}
                </div>
              </button>
            </div>

            {shippingMethod === "emessage" && !isPostcard ? (
              <div className="ph-mask mt-4 rounded-xl border border-input bg-white/70 p-3 shadow-sm">
                <div className="text-sm font-medium">Confirm Facility</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  Select a state, then search for the facility.
                </div>

                <div className="mt-3">
                  <label className="text-xs font-medium text-muted-foreground">State</label>
                  <div className="relative mt-1">
                    <select
                      className="w-full appearance-none rounded-xl border border-input bg-white px-3 py-2 pr-9 text-sm outline-none focus-visible:border-[#4b46a4a3] focus-visible:ring-2 focus-visible:ring-[#4b46a4a3]/25"
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setSelectedFacility(null);
                        setFacilities([]);
                        setQuery("");
                        setFacilitiesLoading(false);
                      }}
                      data-testid="facility-state"
                    >
                      <option value="">Select state…</option>
                      {US_STATES.map((s) => (
                        <option key={s.abbrev} value={s.abbrev}>
                          {s.name} ({s.abbrev})
                        </option>
                      ))}
                    </select>
                    <ChevronDownIcon
                      className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <label className="text-xs font-medium text-muted-foreground">Facility name</label>
                  <div className="relative mt-1">
                    <button
                      type="button"
                      className={`w-full rounded-xl border bg-white px-3 py-2 pr-10 text-left text-sm outline-none transition-colors focus-visible:border-[#4b46a4a3] focus-visible:ring-2 focus-visible:ring-[#4b46a4a3]/25 ${
                        selectedFacility ? "border-emerald-600/60" : "border-input"
                      }`}
                      onClick={openFacilitySearch}
                      data-testid="facility-query"
                    >
                      <div className="text-sm">
                        {selectedFacility?.name ||
                          (query.trim() ? query.trim() : "Search…")}
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {selectedFacility
                          ? "Tap to change facility."
                          : selectedStateAbbrev
                            ? "Tap to search facilities."
                            : "Select a state to search."}
                      </div>
                    </button>

                    {selectedFacility ? (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted/30"
                        aria-label="Clear selected facility"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFacility(null);
                          setQuery("");
                          setFacilities([]);
                          setFacilitiesLoading(false);
                        }}
                        data-testid="facility-clear"
                      >
                        <XIcon className="size-4" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </div>

                {!selectedStateAbbrev ? (
                  <div className="mt-2 text-xs text-muted-foreground">State is required to search.</div>
                ) : null}
              </div>
            ) : null}

            {saveError ? (
              <div className="mt-3 text-xs text-destructive">{saveError}</div>
            ) : null}
          </div>

          {facilitySearchOpen ? (
            <div className="ph-mask absolute inset-0 z-20 flex min-h-0 flex-col rounded-2xl bg-background">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Find a facility</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Choose a state, then search for the facility.
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" onClick={closeFacilitySearch}>
                    Done
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={closeFacilitySearch}
                    aria-label="Close facility search"
                  >
                    <XIcon className="size-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>

              <div
                className="flex min-h-0 flex-1 flex-col bg-muted/20 px-4 py-4"
                style={{
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                  touchAction: "pan-y",
                }}
              >
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">State</label>
                    <div className="relative mt-1">
                      <select
                        className="w-full appearance-none rounded-xl border border-input bg-white px-3 py-2 pr-9 text-sm outline-none focus-visible:border-[#4b46a4a3] focus-visible:ring-2 focus-visible:ring-[#4b46a4a3]/25"
                        value={selectedState}
                        onChange={(e) => {
                          setSelectedState(e.target.value);
                          setSelectedFacility(null);
                          setFacilities([]);
                          setQuery("");
                          setFacilitiesLoading(false);
                        }}
                        data-testid="facility-state-search"
                      >
                        <option value="">Select state…</option>
                        {US_STATES.map((s) => (
                          <option key={s.abbrev} value={s.abbrev}>
                            {s.name} ({s.abbrev})
                          </option>
                        ))}
                      </select>
                      <ChevronDownIcon
                        className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Facility name</label>
                    <div className="relative mt-1">
                      <input
                        ref={inputRef}
                        className={`w-full rounded-xl border bg-white px-3 py-2 pr-10 text-sm outline-none focus-visible:border-[#4b46a4a3] focus-visible:ring-2 focus-visible:ring-[#4b46a4a3]/25 disabled:opacity-70 ${
                          selectedFacility ? "border-emerald-600/60" : "border-input"
                        }`}
                        placeholder={selectedStateAbbrev ? "Search…" : "Select a state first"}
                        value={query}
                        onChange={(e) => {
                          setQuery(e.target.value);
                          setSelectedFacility(null);
                        }}
                        disabled={!selectedStateAbbrev}
                        data-testid="facility-query-input"
                      />

                      {query.trim() ? (
                        <button
                          type="button"
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted/30"
                          aria-label="Clear facility search"
                          onClick={() => {
                            setQuery("");
                            setFacilities([]);
                            setFacilitiesLoading(false);
                            window.setTimeout(() => inputRef.current?.focus(), 0);
                          }}
                          data-testid="facility-query-clear"
                        >
                          <XIcon className="size-4" aria-hidden="true" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div
                  className="mt-3 max-h-[45vh] supports-[height:100dvh]:max-h-[45dvh] overflow-y-auto rounded-xl border border-input bg-white shadow-sm"
                  style={{
                    WebkitOverflowScrolling: "touch",
                    overscrollBehavior: "contain",
                    touchAction: "pan-y",
                  }}
                >
                  {selectedStateAbbrev ? (
                    query.trim().length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        Start typing to see matching facilities.
                      </div>
                    ) : query.trim().length < MIN_FACILITY_QUERY_LENGTH ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        Type at least {MIN_FACILITY_QUERY_LENGTH} characters.
                      </div>
                    ) : facilities.length > 0 ? (
                      <div className="py-1 pr-1">
                        {facilities.map((f) => {
                          const key = String(f?.id ?? f?.name ?? Math.random());
                          const isSelected =
                            selectedFacility &&
                            f &&
                            (selectedFacility?.id != null || f?.id != null) &&
                            selectedFacility?.id === f?.id;
                          return (
                            <button
                              key={key}
                              type="button"
                              className={`w-full px-3 py-2 text-left hover:bg-muted/20 ${isSelected ? "bg-muted/20" : ""}`}
                              onClick={() => onSelectFacility(f)}
                              data-testid="facility-result"
                            >
                              <div className="text-sm">{f?.name}</div>
                            </button>
                          );
                        })}
                      </div>
                    ) : facilitiesLoading ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
                    ) : (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        Facility not found. Check the state and spelling.
                      </div>
                    )
                  ) : (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      Select a state to start searching.
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
            <DialogClose asChild>
              <Button type="button" variant="ghost" disabled={isSaving}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={!canSave || isSaving}
              data-testid="delivery-save"
            >
              {primaryLabel}
            </Button>
          </div>
        </div>

        <Dialog open={truncateConfirmOpen} onOpenChange={setTruncateConfirmOpen} modal={false}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-sm !p-4">
            <DialogTitle className="font-semibold text-base">Photo limit</DialogTitle>
            <DialogDescription className="sr-only">Confirm E-message photo limit.</DialogDescription>
            <div className="mt-2 text-muted-foreground text-sm">
              E-message supports up to {EMESSAGE_MAX_SELECTED_PHOTOS} photos. Continue to keep the first{" "}
              {EMESSAGE_MAX_SELECTED_PHOTOS} and remove the rest.
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <DialogClose asChild>
                <Button type="button" variant="ghost" disabled={isSaving}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                onClick={async () => {
                  if (!canSave) return;
                  setIsSaving(true);
                  setSaveError(null);
                  try {
                    await onSave({ shippingMethod: "emessage", eMessage: selectedFacility });
                    setTruncateConfirmOpen(false);
                    onOpenChange(false);
                  } catch (e) {
                    setSaveError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving}
              >
                Keep first {EMESSAGE_MAX_SELECTED_PHOTOS}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
